import { useState, type ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useUtenteOpzionale } from "@/hooks/useAuth";
import { toast } from "sonner";
import type { AliasEntry, AliasBrand } from "@/data/aliases_glossary";
import { apriEsterno } from "@/lib/apriEsterno";

const BRAND_STYLES: Record<AliasBrand, string> = {
  INIM: "bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-200",
  INIM_EXTRA: "bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-200",
  AJAX: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
  DAITEM: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200",
  NOTIFIER: "bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-200",
};

export async function openManualByName(name: string) {
  if (!name) return;
  try {
    const { data, error } = await supabase.functions.invoke("resolve-pdf", {
      body: { name, page: 1 },
    });
    const risposta = data as { pdf_url?: string | null; error?: string } | null;
    const pdfUrl = risposta?.pdf_url;
    if (risposta?.error === "serve_account" || (error as { status?: number })?.status === 401) {
      toast.error("Serve un account", {
        description: "I manuali dei costruttori sono riservati agli installatori registrati.",
      });
      return;
    }
    if (error || !pdfUrl) {
      toast.error("Manuale non disponibile", {
        description: "Non sono riuscito a recuperare il PDF firmato.",
      });
      return;
    }
    // Il browser di sistema, non `window.open`: dentro la vista web di iOS
    // quella chiamata non fa niente e il manuale non si apre (stessa famiglia
    // del difetto dei bottoni Google/Apple, 08/09/2026). E cosi' non si esce
    // dall'app: la finestra si chiude con «Fine».
    await apriEsterno(pdfUrl);
  } catch {
    toast.error("Errore nell'apertura del manuale");
  }
}

function AliasBody({ entry, onClose }: { entry: AliasEntry; onClose?: () => void }) {
  const related = (entry.related ?? []).slice(0, 3);
  const utente = useUtenteOpzionale();
  // Il glossario e' pubblico (ci si arriva anche senza account), ma i manuali
  // no: meglio dirlo sul pulsante che far scoprire il limite con un errore.
  const hasManual = !!entry.primary_manual && !!utente;
  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-start gap-2">
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] tracking-wide ${BRAND_STYLES[entry.brand]}`}
        >
          {entry.brand}
        </span>
        <div className="min-w-0 font-semibold leading-snug">{entry.extended}</div>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">{entry.spec_key}</p>
      {related.length > 0 && (
        <div className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">Correlati: </span>
          {related.join(", ")}
        </div>
      )}
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className={`w-full gap-1.5 ${!hasManual ? "cursor-not-allowed opacity-50" : ""}`}
        disabled={!hasManual}
        onClick={() => {
          if (!hasManual || !entry.primary_manual) return;
          onClose?.();
          openManualByName(entry.primary_manual);
        }}
      >
        <ExternalLink className="h-3.5 w-3.5" />
        {hasManual
          ? "Apri manuale"
          : entry.primary_manual
            ? "Accedi per aprire il manuale"
            : "Concetto · nessun manuale"}
      </Button>
    </div>
  );
}

export function AliasTooltip({
  entry,
  children,
}: {
  entry: AliasEntry;
  children: ReactNode;
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const trigger = (
    <span className="cursor-help rounded-sm border-b border-dotted border-primary/60 text-primary/95 underline-offset-2 hover:text-primary">
      {children}
    </span>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setOpen(true);
            }}
            className="cursor-help rounded-sm border-b border-dotted border-primary/60 text-primary/95 underline-offset-2"
          >
            {children}
          </button>
        </SheetTrigger>
        <SheetContent side="bottom" className="max-h-[80vh]">
          <div className="pt-4">
            <div className="mb-3 font-mono text-base font-semibold">{entry.sigla}</div>
            <AliasBody entry={entry} onClose={() => setOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <TooltipProvider delayDuration={400} skipDelayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          side="top"
          className="w-[360px] bg-popover p-3 text-popover-foreground"
        >
          <AliasBody entry={entry} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}